import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { debounceTime, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { viewingActions } from './viewing.actions';
import { MessageService } from '@letly/ui-common';
import { ToastSeverity } from '@letly/models';
import { ViewingService } from './viewing.service';
import { Store } from '@ngrx/store';
import { viewingFeature } from './viewing.state';
import { HelpersService } from '@letly/services';
import { selectRouteParam } from '../../router.state';

@Injectable()
export class ViewingEffects {
  private store = inject(Store);
  private actions$ = inject(Actions);
  private messageService = inject(MessageService);
  private viewingService = inject(ViewingService);
  private helperService = inject(HelpersService);

  updateFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(viewingActions.updateFilters),
      map(action => {
        if (action.isLandlord) {
          return viewingActions.loadLandlordViewings({ propertyId: null });
        }
        return viewingActions.loadViewings({ append: false });
      })
    )
  );

  loadViewings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(viewingActions.loadViewings),
      debounceTime(0),
      withLatestFrom(this.store.select(viewingFeature.selectViewingFilters)),
      switchMap(([action, filters]) =>
        this.viewingService.getViewings(this.helperService.cleanFilters(filters)).pipe(
          map(res => {
            if (res.success) {
              return viewingActions.onLoadViewings({
                status: 'success',
                message: null,
                items: res.data.items,
                pageNumber: res.data.pageNumber,
                pageSize: res.data.pageSize,
                totalNumberOfItems: res.data.totalNumberOfItems,
                hasMore: res.data.hasMore,
                append: action && 'append' in action ? action.append : false
              });
            } else {
              this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return viewingActions.onLoadViewings({
                status: 'error',
                message: res.niceErrorMessage,
                items: [],
                pageNumber: 0,
                pageSize: 0,
                totalNumberOfItems: 0,
                hasMore: false
              });
            }
          })
        )
      )
    )
  );

  loadLandlordViewings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(viewingActions.loadLandlordViewings),
      debounceTime(0),
      withLatestFrom(this.store.select(viewingFeature.selectViewingFilters), this.store.select(selectRouteParam('id'))),
      switchMap(([action, filters, routePropertyId]) => {
        const propertyId = (action && 'propertyId' in action && action.propertyId) ? action.propertyId : routePropertyId;
        if (!propertyId) {
          return [];
        }
        return this.viewingService.getLandlordViewingsByPropertyId(propertyId, this.helperService.cleanFilters(filters)).pipe(
          map(res => {
            if (res.success) {
              return viewingActions.onLoadLandlordViewings({
                status: 'success',
                message: null,
                items: res.data.items,
                pageNumber: res.data.pageNumber,
                pageSize: res.data.pageSize,
                totalNumberOfItems: res.data.totalNumberOfItems,
                hasMore: res.data.hasMore,
                append: ('append' in action && action.append) || false
              });
            } else {
              this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return viewingActions.onLoadLandlordViewings({
                status: 'error',
                message: res.niceErrorMessage,
                items: [],
                pageNumber: 0,
                pageSize: 0,
                totalNumberOfItems: 0,
                hasMore: false
              });
            }
          })
        );
      })
    )
  );

  loadAllLandlordViewings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(viewingActions.loadAllLandlordViewings),
      debounceTime(0),
      withLatestFrom(this.store.select(viewingFeature.selectViewingAllLandlordFilters)),
      switchMap(([action, filters]) =>
        this.viewingService.getAllLandlordAppointments(this.helperService.cleanFilters(filters)).pipe(
          map(res => {
            if (res.success) {
              return viewingActions.onLoadAllLandlordViewings({
                status: 'success',
                message: null,
                items: res.data.items,
                pageNumber: res.data.pageNumber,
                pageSize: res.data.pageSize,
                totalNumberOfItems: res.data.totalNumberOfItems,
                hasMore: res.data.hasMore,
                append: action.append
              });
            } else {
              this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return viewingActions.onLoadAllLandlordViewings({
                status: 'error',
                message: res.niceErrorMessage,
                items: [],
                pageNumber: 0,
                pageSize: 0,
                totalNumberOfItems: 0,
                hasMore: false
              });
            }
          })
        )
      )
    )
  );

  loadViewingDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(viewingActions.loadViewingDetail),
      mergeMap(action => {
        const request$ = action.isLandlord
          ? this.viewingService.getLandlordAppointmentById(action.id)
          : this.viewingService.getViewingById(action.id);

        return request$.pipe(
          map(res => {
            if (res.success) {
              return viewingActions.onLoadViewingDetail({
                status: 'success',
                message: null,
                id: action.id,
                viewing: res.data
              });
            } else {
              this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return viewingActions.onLoadViewingDetail({
                status: 'error',
                message: res.niceErrorMessage,
                id: action.id,
                viewing: null
              });
            }
          })
        );
      })
    )
  );

  cancelViewing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(viewingActions.cancelViewing),
      switchMap(({ id, body, isLandlord }) => {
        const request$ = isLandlord
          ? this.viewingService.cancelLandlordViewing(id, body)
          : this.viewingService.cancelViewing(id, body);

        return request$.pipe(
          switchMap(res => {
            if (res.success) {
              this.messageService.showSnackbar($localize`:@@viewings.toast.cancel.success:Viewing cancelled.`, ToastSeverity.Success);
              return [
                viewingActions.onCancelViewing({
                  id,
                  status: 'success',
                  message: '',
                  viewing: res.data
                }),
                viewingActions.loadViewingDetail({ id, isLandlord })
              ];
            } else {
              this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return [
                viewingActions.onCancelViewing({
                  id,
                  status: 'error',
                  message: res.niceErrorMessage,
                  viewing: null
                })
              ];
            }
          })
        );
      })
    )
  );

  confirmViewing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(viewingActions.confirmViewing),
      switchMap(({ id }) =>
        this.viewingService.confirmViewing(id).pipe(
          map(res => {
            if (res.success) {
              this.messageService.showSnackbar($localize`:@@viewings.toast.confirm.success:Viewing confirmed.`, ToastSeverity.Success);
              return viewingActions.onConfirmViewing({
                id,
                status: 'success',
                message: '',
                viewing: res.data
              });
            } else {
              this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return viewingActions.onConfirmViewing({
                id,
                status: 'error',
                message: res.niceErrorMessage,
                viewing: null
              });
            }
          })
        )
      )
    )
  );

  rejectViewing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(viewingActions.rejectViewing),
      switchMap(({ id, reason }) =>
        this.viewingService.rejectViewing(id, reason).pipe(
          map(res => {
            if (res.success) {
              this.messageService.showSnackbar($localize`:@@viewings.toast.reject.success:Viewing declined.`, ToastSeverity.Success);
              return viewingActions.onRejectViewing({
                id,
                status: 'success',
                message: '',
                viewing: res.data
              });
            } else {
              this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return viewingActions.onRejectViewing({
                id,
                status: 'error',
                message: res.niceErrorMessage,
                viewing: null
              });
            }
          })
        )
      )
    )
  );

  completeViewing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(viewingActions.completeViewing),
      switchMap(({ id, note }) =>
        this.viewingService.completeViewing(id, note).pipe(
          map(res => {
            if (res.success) {
              this.messageService.showSnackbar($localize`:@@viewings.toast.complete.success:Viewing marked as complete.`, ToastSeverity.Success);
              return viewingActions.onCompleteViewing({
                id,
                status: 'success',
                message: '',
                viewing: res.data
              });
            } else {
              this.messageService.showSnackbar(res.niceErrorMessage, ToastSeverity.Error);
              return viewingActions.onCompleteViewing({
                id,
                status: 'error',
                message: res.niceErrorMessage,
                viewing: null
              });
            }
          })
        )
      )
    )
  );
}
